import * as Yup from "yup";
import "./becomeReferrer.css";
import { useState } from "react";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import Toaster from "../../components/Toast/Toast";
import TextInput from "../../components/TextInput/TextInput";
import { referrerProgram } from "../../api/modules/b2droppie.class";

const BecomeReferrer = () => {
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  const initialValues = {
    email: "",
    first_name: "",
    last_name: "",
    username: "",
    phone_number: "",
    message: "",
  };

  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),

    first_name: Yup.string()
      .max(15, "First name cannot exceed 15 characters")
      .required("First name is required"),

    last_name: Yup.string()
      .max(15, "Last name cannot exceed 15 characters")
      .required("Last name is required"),

    username: Yup.string().required("Username is required"),

    phone_number: Yup.string()
      .nullable()
      .matches(
        /^\+?[0-9]{8,15}$/,
        "Phone number must contain 8 to 15 digits and may start with +",
      ),

    message: Yup.string().required("Message is required"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const res = await referrerProgram(values);
      setError({
        success: res?.data?.message || "Request Submitted Successfully",
        error: "",
      });

      resetForm();
    } catch (err) {
      console.error("Referrer error:", err);
      const backendErrors = err?.response?.data?.error?.message;
      let errorMessage = "Something went wrong. Please try again.";
      if (backendErrors) {
        errorMessage = Object.values(backendErrors).flat().join(" ");
      }

      setError({
        success: "",
        error: errorMessage,
      });
    }
  };

  return (
    <>
      <Toaster message={error} />
      <div
        className={`become-referrer-main px-4 px-md-5 pt-5 mb-3 ${
          isDarkMode ? "become-referrer-dark" : "become-referrer-light"
        }`}
      >
        <h1
          className={`${isDarkMode ? "text-lavender-gray" : "text-deep-purple"}`}
        >
          Become a Part of Referral Program
        </h1>
        <div className="become-referrer-p">
          <p>
            Join our Referral Program and earn a <strong>5% of Deposit</strong>{" "}
            by referring new users to our platform. This program is designed for
            individuals who want to grow with us by sharing opportunities within
            their network.
          </p>

          <p>
            As a referrer, you will receive{" "}
            <strong>5% of the Deposit generated</strong> from users you
            successfully refer. There is no limit to your earnings — the more
            active users you bring, the more you can earn.
          </p>

          <p>
            Please fill out the form below with accurate information and briefly
            explain why you want to become a referrer. Our team will review your
            request and get back to you within <strong>24 hours</strong>.
            Response times may vary during high traffic periods.
          </p>
        </div>

        <Formik
          enableReinitialize
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={SignupSchema}
        >
          {({ values, errors, touched, handleChange, handleSubmit }) => (
            <Form className="contact-form p-0" onSubmit={handleSubmit}>
              <div className="become-referrer-textfeilds">
                <Row>
                  <Col className="mt-2" md={6}>
                    <TextInput
                      type="text"
                      name="first_name"
                      label="First Name"
                      isDarkMode={isDarkMode}
                      onChange={handleChange}
                      value={values.first_name}
                      placeholder="Enter your First Name"
                      className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    />
                    {touched.first_name && errors.first_name && (
                      <span className="error">{errors.first_name}</span>
                    )}
                  </Col>

                  <Col className="mt-2" md={6}>
                    <TextInput
                      type="text"
                      name="last_name"
                      label="Last Name"
                      isDarkMode={isDarkMode}
                      onChange={handleChange}
                      value={values.last_name}
                      placeholder="Enter your Last Name"
                      className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    />
                    {touched.last_name && errors.last_name && (
                      <span className="error">{errors.last_name}</span>
                    )}
                  </Col>

                  <Col className="mt-2" md={6}>
                    <TextInput
                      type="text"
                      name="username"
                      label="Username"
                      isDarkMode={isDarkMode}
                      onChange={handleChange}
                      value={values.username}
                      placeholder="Enter your Username"
                      className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    />
                    {touched.username && errors.username && (
                      <span className="error">{errors.username}</span>
                    )}
                  </Col>

                  <Col className="mt-2" md={6}>
                    <TextInput
                      type="email"
                      name="email"
                      label="Email"
                      value={values.email}
                      isDarkMode={isDarkMode}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    />
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </Col>

                  <Col className="mt-2" md={12}>
                    <TextInput
                      type="text"
                      name="phone_number"
                      label="Phone Number"
                      isDarkMode={isDarkMode}
                      onChange={handleChange}
                      placeholder="+1234567890"
                      value={values.phone_number}
                      className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    />
                    {touched.phone_number && errors.phone_number && (
                      <span className="error">{errors.phone_number}</span>
                    )}
                  </Col>
                </Row>

                <div className="mt-2">
                  <TextInput
                    textarea
                    type="text"
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    isDarkMode={isDarkMode}
                    label="Why you want to become a Referrer?"
                    className={`${isDarkMode ? "dark-input-bg" : "light-input-bg"}`}
                    placeholder="Please ensure your description is accurate and thorough."
                  />
                  {touched.message && errors.message && (
                    <span className="error">{errors.message}</span>
                  )}
                </div>
              </div>
              <div className="d-end">
                <button
                  type="submit"
                  className={`w-10 w-md-100 mt-4 ${
                    isDarkMode
                      ? "filled-medium-deep-yellow-btn-dark"
                      : "filled-medium-deep-purple-btn-light"
                  }`}
                >
                  <span>Submit</span>
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default BecomeReferrer;
